export interface LegacyHandicapBoat {
    sail: string
    handicap: string
}

export interface LegacyHandicapClass {
    boatClassId: string
    name: string
    boats: LegacyHandicapBoat[]
}

export interface LegacyHandicapGroup {
    id: string
    label: string
    classes: LegacyHandicapClass[]
}

export type LegacyHandicapAssignments = Record<string, number>

interface ExportedBoatRace {
    race?: number | null
    eventRaceId?: string | null
    handicapSelected?: string | null
    handicapValue?: number | null
}

interface ExportedBoat {
    boatClassId: string
    country: string
    sailNumber: string
    eventBoatRaces?: ExportedBoatRace[]
}

export interface ExportedContent {
    event: {
        boats: ExportedBoat[]
        classes: Array<{ boatClassId: string; shortcut?: string | null; className?: string | null }>
        races: Array<{ race: number }>
    }
}

export function legacyHandicapGroups(content: ExportedContent): LegacyHandicapGroup[] {
    const groups = new Map<string, LegacyHandicapGroup>()
    const classNames = new Map(content.event.classes.map(eventClass => [
        eventClass.boatClassId,
        eventClass.className || eventClass.shortcut || eventClass.boatClassId,
    ]))

    for (const boat of content.event.boats) {
        for (const boatRace of boat.eventBoatRaces ?? []) {
            if (Object.hasOwn(boatRace, 'race') || !boatRace.eventRaceId)
                continue

            let group = groups.get(boatRace.eventRaceId)
            if (!group) {
                const index = groups.size
                const label = String.fromCharCode(65 + (index + 23) % 26)
                    + (index >= 26 ? String(Math.floor(index / 26) + 1) : '')
                group = { id: boatRace.eventRaceId, label, classes: [] }
                groups.set(boatRace.eventRaceId, group)
            }

            const name = classNames.get(boat.boatClassId) ?? boat.boatClassId
            let boatClass = group.classes.find(item => item.boatClassId === boat.boatClassId)
            if (!boatClass) {
                boatClass = { boatClassId: boat.boatClassId, name, boats: [] }
                group.classes.push(boatClass)
            }

            boatClass.boats.push({
                sail: `${boat.country} ${boat.sailNumber}`,
                handicap: boatRace.handicapSelected ?? String(boatRace.handicapValue ?? '—'),
            })
        }
    }

    return [...groups.values()]
}

export function validateLegacyHandicapAssignments(
    content: ExportedContent,
    assignments: LegacyHandicapAssignments = {},
): void {
    const races = new Set(content.event.races.map(race => race.race))
    for (const group of legacyHandicapGroups(content)) {
        if (!Object.hasOwn(assignments, group.id) || !races.has(assignments[group.id]))
            throw new Error(`Missing or invalid race assignment for legacy handicap ${group.id}`)
    }
}
